import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, Button, Paper, CircularProgress, Chip, IconButton, TextField, Checkbox, Alert, Tooltip, LinearProgress, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FastForwardIcon from '@mui/icons-material/FastForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import GeminiIcon from './GeminiIcon';
import { API_BASE_URL } from '../config';

/**
 * InteractiveDailyActivityBuilder
 * 
 * Allows users to build their itinerary day-by-day interactively:
 * - Shows suggested activities for ONE day at a time
 * - User selects which activities they want
 * - User can request more suggestions
 * - User can use chat to modify activities
 * - User confirms day and moves to next
 * - Option to let AI auto-complete remaining days
 */
const InteractiveDailyActivityBuilder = ({ 
    tripId, 
    tripData, 
    onComplete, 
    onCancel,
    onClose,
    setIsAiWorking,
    handleShowAiMessage,
    initialDayIndex = 0,      // Start at specific day
    singleDayMode = false,    // If true, only allow adding to one day
    onOpenAiChat,             // Callback to open AI chat for specific day: (dayNumber) => void
    existingActivities = {},  // Activities already in itinerary: { date: [activities] }
    autoLoad = false          // If true, auto-load suggestions on mount/day change; if false, user must click
}) => {
    const { t, i18n } = useTranslation();
    
    // State
    const [currentDayIndex, setCurrentDayIndex] = useState(initialDayIndex);
    const [suggestedActivities, setSuggestedActivities] = useState([]);
    const [selectedActivityIds, setSelectedActivityIds] = useState(new Set());
    const [confirmedDays, setConfirmedDays] = useState({}); // { dayIndex: [activityIds] }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chatInput, setChatInput] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [isAutoPlanning, setIsAutoPlanning] = useState(false);
    const [alreadySuggestedIds, setAlreadySuggestedIds] = useState([]);
    const [activityImages, setActivityImages] = useState({}); // { activity_key: { image_url, google_images_url } }
    const [showActivityList, setShowActivityList] = useState(false); // Show activity list dialog
    
    // Derive trip days from route
    const tripDays = tripData?.route || [];
    const totalDays = tripDays.length;
    const currentDay = tripDays[currentDayIndex] || {};
    const isLastDay = singleDayMode || currentDayIndex === totalDays - 1;
    // Flight days can still have activities (e.g., things to do before/after flight)
    const isFlightDay = false; // Disabled - allow activity suggestions for all days
    const hasFlightInCity = currentDay.city?.includes('->') || false;

    // Compute the ISO date key for this day, matching the activitiesByDay keys in the parent
    const currentDayDate = useMemo(() => {
        const dayNumber = currentDay.day ?? (currentDayIndex + 1);
        if (tripData?.start_date) {
            const start = new Date(tripData.start_date);
            start.setDate(start.getDate() + dayNumber - 1);
            return start.toISOString().split('T')[0];
        }
        return `day-${dayNumber}`;
    }, [tripData, currentDay, currentDayIndex]);
    
    // Map of common airport codes to city names
    const airportToCityMap = {
        'SHA': 'Shanghai', 'PVG': 'Shanghai',
        'PEK': 'Beijing', 'PKX': 'Beijing',
        'HKG': 'Hong Kong',
        'TLV': 'Tel Aviv',
        'JFK': 'New York', 'LGA': 'New York', 'EWR': 'New York',
        'LAX': 'Los Angeles',
        'LHR': 'London', 'LGW': 'London', 'STN': 'London',
        'CDG': 'Paris', 'ORY': 'Paris',
        'NRT': 'Tokyo', 'HND': 'Tokyo',
        'SIN': 'Singapore',
        'BKK': 'Bangkok',
        'DXB': 'Dubai',
        'FCO': 'Rome', 'CIA': 'Rome',
        'BCN': 'Barcelona',
        'AMS': 'Amsterdam',
        'FRA': 'Frankfurt',
        'MUC': 'Munich',
        'ZRH': 'Zurich',
        'VIE': 'Vienna',
        'MAD': 'Madrid',
        'LIS': 'Lisbon',
        'ATH': 'Athens',
        'IST': 'Istanbul',
        'SYD': 'Sydney',
        'MEL': 'Melbourne',
        'YYZ': 'Toronto',
        'YVR': 'Vancouver',
        'SFO': 'San Francisco',
        'SEA': 'Seattle',
        'MIA': 'Miami',
        'ORD': 'Chicago',
        'DFW': 'Dallas',
        'DEN': 'Denver',
        'BOS': 'Boston',
        'ICN': 'Seoul', 'GMP': 'Seoul',
        'TPE': 'Taipei',
        'DEL': 'New Delhi',
        'BOM': 'Mumbai',
        'CAI': 'Cairo',
        'JNB': 'Johannesburg',
        'GRU': 'São Paulo',
        'EZE': 'Buenos Aires',
        'MEX': 'Mexico City',
    };

    // Extract destination city from flight label (e.g., "TLV (Israel) -> SHA (China)" -> "Shanghai, China")
    // This function can be called with any day object
    const getLocationForDay = (day) => {
        const isFlightLabel = day.city?.includes('->');
        if (!isFlightLabel) {
            return `${day.city}, ${day.country}`;
        }
        // For flight days, extract destination from the flight label
        const parts = day.city.split('->');
        if (parts.length >= 2) {
            const destinationPart = parts[1].trim(); // e.g., "SHA (China)"
            // Extract country from parentheses
            const countryMatch = destinationPart.match(/\(([^)]+)\)/);
            const country = countryMatch ? countryMatch[1] : '';
            // Get the airport code
            const airportCode = destinationPart.split('(')[0].trim();
            const cityName = airportToCityMap[airportCode] || airportCode;
            return country ? `${cityName}, ${country}` : cityName;
        }
        return `${day.city}, ${day.country}`;
    };

    // Convenience function for current day
    const getActivityLocation = () => getLocationForDay(currentDay);
    
    // Calculate progress (in single day mode, show 0% to 100% for just this day)
    const confirmedDayCount = Object.keys(confirmedDays).length;
    const progressPercent = singleDayMode 
        ? (confirmedDays[currentDayIndex] ? 100 : 0)
        : (totalDays > 0 ? (confirmedDayCount / totalDays) * 100 : 0);
    
    // Calculate day intensity (total hours including buffers between activities)
    const calculateDayIntensity = useCallback(() => {
        const BUFFER_MINUTES = 60; // 60 minutes between activities
        
        // Get selected activities from suggestions
        const selectedActivities = suggestedActivities.filter(a => selectedActivityIds.has(a.activity_id));
        
        // Get existing activities for the current day from the itinerary
        const existingDayActivities = existingActivities[currentDayDate] || [];
        
        // Sum up durations from both existing and selected activities
        let totalMinutes = 0;
        
        // Add existing activities duration
        existingDayActivities.forEach(activity => {
            // Skip flight and hotel/lodging activities from the calculation
            if (activity.type === 'flight' || activity.type === 'hotel' || activity.type === 'accommodation' || activity.type === 'lodging' || activity.type === 'Lodging') return;
            totalMinutes += activity.duration || 60;
        });
        
        // Add selected suggestion durations
        selectedActivities.forEach(activity => {
            totalMinutes += activity.duration_minutes || 60;
        });
        
        // Total activity count (excluding flights and hotels/lodging)
        const existingCount = existingDayActivities.filter(a => a.type !== 'flight' && a.type !== 'hotel' && a.type !== 'accommodation' && a.type !== 'lodging' && a.type !== 'Lodging').length;
        const selectedCount = selectedActivities.length;
        const activityCount = existingCount + selectedCount;
        
        // Add buffer time between activities (n-1 buffers for n activities)
        if (activityCount > 1) {
            totalMinutes += (activityCount - 1) * BUFFER_MINUTES;
        }
        
        const totalHours = totalMinutes / 60;
        
        // Determine intensity level
        let intensity = 'easy';
        let color = '#4caf50'; // Green
        if (totalHours > 8) {
            intensity = 'intense';
            color = '#f44336'; // Red
        } else if (totalHours > 5) {
            intensity = 'moderate';
            color = '#ff9800'; // Orange/Yellow
        }
        
        return {
            totalMinutes,
            totalHours: Math.round(totalHours * 10) / 10, // Round to 1 decimal
            activityCount,
            existingCount,
            selectedCount,
            intensity,
            color
        };
    }, [suggestedActivities, selectedActivityIds, existingActivities, currentDayDate]);
    
    const dayIntensity = calculateDayIntensity();
    
    // Load activities for current day
    const loadDayActivities = useCallback(async (dayIndex) => {
        if (!tripId || !tripDays[dayIndex]) return;
        
        const day = tripDays[dayIndex];
        setLoading(true);
        setError(null);
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/trips/get_daily_activities.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    trip_id: tripId,
                    day_index: dayIndex,
                    city: day.city,
                    country: day.country,
                    completed_days: Object.values(confirmedDays).flat(),
                    trip_data: {
                        travel_pace: tripData?.travel_pace,
                        trip_interests: tripData?.trip_interests
                    },
                    language: i18n.language
                })
            });
            
            const data = await response.json();
            
            if (data.success && data.suggested_activities) {
                setSuggestedActivities(data.suggested_activities);
                setAlreadySuggestedIds(data.suggested_activities.map(a => a.activity_id));
                
                // Don't pre-select any activities - let user choose
                setSelectedActivityIds(new Set());
            } else {
                setError(data.error || t('failed_to_load_activities'));
            }
        } catch (err) {
            console.error('Error loading day activities:', err);
            setError(t('failed_to_load_activities'));
        } finally {
            setLoading(false);
        }
    }, [tripId, tripDays, confirmedDays, tripData, i18n.language, t]);
    
    // Load activities when day changes (only when autoLoad is enabled)
    useEffect(() => {
        if (tripDays.length > 0) {
            if (autoLoad && !isFlightDay) {
                loadDayActivities(currentDayIndex);
            } else {
                // Clear suggestions when switching days without auto-loading
                setSuggestedActivities([]);
                setSelectedActivityIds(new Set());
                setError(null);
            }
        }
    }, [currentDayIndex, tripDays.length, isFlightDay, autoLoad, loadDayActivities]);
    
    // Toggle activity selection
    const handleToggleActivity = (activityId) => {
        setSelectedActivityIds(prev => {
            const next = new Set(prev);
            if (next.has(activityId)) {
                next.delete(activityId);
            } else {
                next.add(activityId);
            }
            return next;
        });
    };
    
    // Get more suggestions
    const handleGetMoreSuggestions = async () => {
        if (!tripId || !currentDay) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/trips/get_alternative_activities.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    trip_id: tripId,
                    day_index: currentDayIndex,
                    city: currentDay.city,
                    country: currentDay.country,
                    already_suggested_ids: alreadySuggestedIds,
                    already_selected_ids: Array.from(selectedActivityIds),
                    language: i18n.language
                })
            });
            
            const data = await response.json();
            
            if (data.success && data.alternative_activities) {
                // Add new alternatives to suggestions
                setSuggestedActivities(prev => [...prev, ...data.alternative_activities]);
                setAlreadySuggestedIds(prev => [...prev, ...data.alternative_activities.map(a => a.activity_id)]);
            } else {
                setError(data.error || t('failed_to_load_alternatives'));
            }
        } catch (err) {
            console.error('Error loading alternatives:', err);
            setError(t('failed_to_load_alternatives'));
        } finally {
            setLoading(false);
        }
    };
    
    // Process chat instruction
    const handleChatSubmit = async () => {
        if (!chatInput.trim() || !tripId) return;
        
        const userMessage = chatInput.trim();
        setChatInput('');
        setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
        setLoading(true);
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/trips/process_day_instruction.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    trip_id: tripId,
                    day_index: currentDayIndex,
                    instruction: userMessage,
                    current_day: currentDay,
                    selected_activities: suggestedActivities.filter(a => selectedActivityIds.has(a.activity_id)),
                    language: i18n.language
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                setChatHistory(prev => [...prev, { role: 'assistant', content: data.response }]);
                
                // Update activities if modified
                if (data.updated_activities) {
                    setSuggestedActivities(data.updated_activities);
                    setAlreadySuggestedIds(data.updated_activities.map(a => a.activity_id));
                }
                
                // Update selection if modified
                if (data.updated_selected_ids) {
                    setSelectedActivityIds(new Set(data.updated_selected_ids));
                }
            } else {
                setChatHistory(prev => [...prev, { 
                    role: 'assistant', 
                    content: data.error || t('chat_error')
                }]);
            }
        } catch (err) {
            console.error('Error processing instruction:', err);
            setChatHistory(prev => [...prev, { 
                role: 'assistant', 
                content: t('chat_error')
            }]);
        } finally {
            setLoading(false);
        }
    };
    
    // Confirm current day and move to next
    // Helper function to schedule activities intelligently
    const scheduleActivitiesIntelligently = (activities) => {
        // Define category priority for time-of-day scheduling
        // Morning activities: nature, adventure, sightseeing, highlights
        // Midday: culture, shopping, food
        // Evening: nightlife, relaxation
        const categoryTimeSlots = {
            // Morning (9:00-12:00) - Outdoor/active activities
            'highlight': 1, // Must-see attractions first
            'nature': 1,
            'adventure': 1,
            'sightseeing': 2,
            'cultural': 2,
            // Afternoon (12:00-17:00)
            'food': 3,
            'culture': 3, // May come as 'culture' instead of 'cultural'
            'experience': 3,
            'shopping': 4,
            'other': 4,
            // Evening (17:00+)
            'relaxation': 5,
            'nightlife': 6
        };

        // Sort activities by their appropriate time slot
        const sortedActivities = [...activities].sort((a, b) => {
            const slotA = categoryTimeSlots[a.category] || categoryTimeSlots[a.section] || 3;
            const slotB = categoryTimeSlots[b.category] || categoryTimeSlots[b.section] || 3;
            return slotA - slotB;
        });

        // Calculate start times with travel/personal buffer (30 min between activities)
        const BUFFER_MINUTES = 30; // Travel + personal time between activities
        let currentTime = 9 * 60; // Start at 9:00 AM (in minutes from midnight)
        
        return sortedActivities.map(activity => {
            const duration = activity.duration_minutes || 60;
            const startHour = Math.floor(currentTime / 60);
            const startMin = currentTime % 60;
            const startTimeStr = `${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}:00`;
            
            // Move current time forward
            currentTime += duration + BUFFER_MINUTES;
            
            return {
                ...activity,
                scheduled_start_time: startTimeStr
            };
        });
    };

    const handleConfirmDay = async () => {
        const token = localStorage.getItem('token');
        
        // Handle flight days - create flight activity automatically
        if (isFlightDay) {
            try {
                // Parse flight info from city label (e.g., "TLV (Israel) -> SHA (China)")
                const flightLabel = currentDay.city || '';
                const isFirstDay = currentDayIndex === 0;
                const isLastDayOfTrip = currentDayIndex === totalDays - 1;
                
                // Determine flight type and time
                const startTime = isFirstDay ? '06:00:00' : '18:00:00';
                const flightType = isFirstDay ? 'Departure' : 'Return';
                
                console.log(`Creating ${flightType} flight activity for day ${currentDayIndex + 1}: ${flightLabel}`);
                
                const response = await fetch(`${API_BASE_URL}/activities.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        trip_id: tripId,
                        day: currentDayIndex + 1, // 1-indexed day
                        title: flightLabel,
                        location: 'Airport',
                        start_time: startTime,
                        duration: 480, // Default 8 hours for flights
                        notes: `Flight: ${flightLabel}`,
                        type: 'flight',
                        is_ai_suggestion: 1,
                        is_pinned: 1
                    })
                });
                
                if (!response.ok) {
                    const errorData = await response.json();
                    console.error('Failed to create flight activity:', errorData);
                } else {
                    console.log('Successfully created flight activity');
                }
            } catch (err) {
                console.error('Error creating flight activity:', err);
            }
        } else {
            // Save selected activities for this day (regular day)
            const selected = suggestedActivities.filter(a => selectedActivityIds.has(a.activity_id));
            
            // Schedule activities intelligently (by category/time-of-day, with buffers)
            const scheduledActivities = scheduleActivitiesIntelligently(selected);
            
            // Save activities to database
            try {
                for (const activity of scheduledActivities) {
                    const response = await fetch(`${API_BASE_URL}/activities.php`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            trip_id: tripId,
                            day: currentDayIndex + 1, // 1-indexed day
                            title: activity.name,
                            location: activity.location || getActivityLocation(),
                            start_time: activity.scheduled_start_time,
                            duration: activity.duration_minutes || 60, // Backend expects 'duration' not 'duration_min'
                            notes: activity.description || '',
                            type: activity.category || 'activity',
                            is_ai_suggestion: 1
                        })
                    });
                    
                    if (!response.ok) {
                        const errorData = await response.json();
                        console.error('Failed to save activity:', activity.name, errorData);
                    }
                }
            } catch (err) {
                console.error('Error saving activities:', err);
            }
        }
        
        // Update confirmed days
        setConfirmedDays(prev => ({
            ...prev,
            [currentDayIndex]: Array.from(selectedActivityIds)
        }));
        
        // Move to next day or complete
        if (isLastDay) {
            onComplete?.();
        } else {
            setCurrentDayIndex(prev => prev + 1);
            setSuggestedActivities([]);
            setSelectedActivityIds(new Set());
            setChatHistory([]);
            setAlreadySuggestedIds([]);
        }
    };
    
    // Auto-plan remaining days
    const handleAutoPlanRemaining = async () => {
        setIsAutoPlanning(true);
        setError(null);
        
        try {
            const token = localStorage.getItem('token');
            
            // Build remaining days with existing activity counts
            // Skip days that already have 3+ activities
            const TARGET_ACTIVITIES = 3;
            const remainingDays = tripDays.slice(currentDayIndex)
                .map((day, idx) => {
                    const existingCount = (existingActivities[day.date] || []).filter(a => a.type !== 'flight').length;
                    return {
                        ...day,
                        day_index: currentDayIndex + idx,
                        existing_count: existingCount,
                        activities_needed: Math.max(0, TARGET_ACTIVITIES - existingCount)
                    };
                })
                .filter(day => day.activities_needed > 0); // Skip days already at target
            
            if (remainingDays.length === 0) {
                // All days already have enough activities
                onComplete?.();
                return;
            }
            
            const response = await fetch(`${API_BASE_URL}/trips/auto_plan_remaining_days.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    trip_id: tripId,
                    completed_days: Object.values(confirmedDays).flat(),
                    remaining_days: remainingDays,
                    trip_data: {
                        travel_pace: tripData?.travel_pace,
                        trip_interests: tripData?.trip_interests
                    },
                    language: i18n.language
                })
            });
            
            const data = await response.json();
            
            if (data.success && data.auto_planned_days) {
                // Save all auto-planned activities
                const BUFFER_MINUTES = 30; // 30-min travel buffer between activities
                for (const day of data.auto_planned_days) {
                    // Compute sequential start times: first at 9 AM, each subsequent after previous ends + 30 min
                    let currentTime = 9 * 60; // 9:00 AM in minutes from midnight
                    for (const activity of day.activities) {
                        const duration = activity.duration_minutes || 60;
                        const startHour = Math.floor(currentTime / 60);
                        const startMin = currentTime % 60;
                        const startTime = `${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}:00`;

                        // Advance clock: duration + 30 min travel buffer
                        currentTime += duration + BUFFER_MINUTES;
                        
                        await fetch(`${API_BASE_URL}/activities.php`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                trip_id: tripId,
                                day: day.day_index + 1,
                                title: activity.name,
                                location: activity.location || getLocationForDay(day),
                                start_time: startTime,
                                duration: duration, // Backend expects 'duration' not 'duration_min'
                                notes: activity.description || '',
                                type: activity.category || 'activity',
                                is_ai_suggestion: 1
                            })
                        });
                    }
                }
                
                onComplete?.();
            } else {
                setError(data.error || t('auto_plan_failed'));
            }
        } catch (err) {
            console.error('Error auto-planning:', err);
            setError(t('auto_plan_failed'));
        } finally {
            setIsAutoPlanning(false);
        }
    };
    
    // Go back to previous day
    const handleGoBack = () => {
        if (currentDayIndex > 0) {
            // Remove previous day from confirmed
            const newConfirmed = { ...confirmedDays };
            delete newConfirmed[currentDayIndex - 1];
            setConfirmedDays(newConfirmed);
            
            setCurrentDayIndex(prev => prev - 1);
            setSuggestedActivities([]);
            setSelectedActivityIds(new Set());
            setChatHistory([]);
            setAlreadySuggestedIds([]);
        }
    };
    
    // Format time helper
    const formatTime = (time) => {
        if (!time) return '';
        return time.slice(0, 5); // HH:MM
    };
    
    // Open Google Images search in a popup window
    const handleOpenGoogleImages = (e, activityName, city, country) => {
        e.stopPropagation(); // Prevent checkbox toggle
        const searchQuery = encodeURIComponent(`${activityName} ${city} ${country}`);
        const googleImagesUrl = `https://www.google.com/search?tbm=isch&q=${searchQuery}`;
        window.open(googleImagesUrl, 'GoogleImages', 'width=1000,height=700,scrollbars=yes,resizable=yes');
    };
    
    // Track which images are being fetched to prevent duplicate requests
    const fetchingImages = useRef(new Set());
    
    // Fetch preview image for an activity
    const fetchActivityImage = useCallback(async (activityName, city, country) => {
        // Guard against undefined values
        if (!activityName || !city) return;
        
        const key = `${activityName.toLowerCase()}|${city.toLowerCase()}`;
        
        // Already fetching or fetched
        if (fetchingImages.current.has(key)) return;
        fetchingImages.current.add(key);
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/trips/get_activity_preview_image.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    activity_name: activityName,
                    city: city,
                    country: country
                })
            });
            
            const data = await response.json();
            console.log('Activity image response for', activityName, ':', data);
            if (data.success && data.image_url) {
                setActivityImages(prev => ({
                    ...prev,
                    [key]: {
                        image_url: data.image_url,
                        google_images_url: data.google_images_url
                    }
                }));
            } else {
                console.warn('No image returned for', activityName, ':', data.error || 'unknown');
            }
        } catch (err) {
            console.error('Error fetching activity image:', err);
        }
    }, []);
    
    // Fetch images for suggested activities
    useEffect(() => {
        if (suggestedActivities.length > 0 && currentDay.city) {
            suggestedActivities.forEach(activity => {
                fetchActivityImage(activity.name, currentDay.city, currentDay.country);
            });
        }
    }, [suggestedActivities, currentDay.city, currentDay.country, fetchActivityImage]);
    
    // Get image URL for an activity
    const getActivityImage = (activityName, city) => {
        // Guard against undefined values
        if (!activityName || !city) return null;
        
        const key = `${activityName.toLowerCase()}|${city.toLowerCase()}`;
        return activityImages[key] || null;
    };
    
    return (
        <div className="tw-max-w-3xl tw-mx-auto tw-p-2 sm:tw-p-4">
            {/* Header with progress */}
            {!singleDayMode && (
            <div className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-px-4 tw-py-3 tw-mb-3">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-flex-wrap tw-gap-2">
                    <span className="tw-text-base tw-font-bold tw-text-muted-fore">
                        {t('build_your_itinerary') || 'Build Your Itinerary'}
                    </span>
                    <span className="tw-text-xs tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-full tw-bg-accent/20 tw-text-accent-glow">
                        {confirmedDayCount}/{totalDays} {t('days') || 'days'}
                    </span>
                </div>
                <div className="tw-h-2 tw-rounded-full tw-bg-surface-3">
                    <div
                        className="tw-h-2 tw-rounded-full tw-bg-accent tw-transition-all tw-duration-500"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>
            </div>
            )}

            {/* Current day header */}
            <div className="tw-rounded-2xl tw-bg-accent/15 tw-border tw-border-accent/25 tw-px-4 tw-py-3 tw-mb-3">
                <h2 className="tw-text-lg sm:tw-text-xl tw-font-bold tw-text-muted-fore">
                    {t('day') || 'Day'} {currentDayIndex + 1}: {currentDay.city || t('travel_day') || 'Travel Day'}
                </h2>
                {currentDay.country && (
                    <p className="tw-text-sm tw-text-accent-glow tw-mt-0.5 tw-flex tw-items-center tw-gap-1">
                        <LocationOnIcon sx={{ fontSize: 13 }} />
                        {currentDay.country}
                    </p>
                )}
            </div>

            {/* Error message */}
            {error && (
                <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-bg-red-900/20 tw-border tw-border-red-500/30 tw-px-4 tw-py-3 tw-mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-text-red-400 tw-shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>
                    <p className="tw-text-sm tw-text-red-300 tw-flex-1">{error}</p>
                    <button onClick={() => setError(null)} className="tw-text-muted tw-hover:tw-text-red-400 tw-transition-colors tw-p-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>
            )}

            {/* Flight day info banner */}
            {hasFlightInCity && (
                <div className="tw-rounded-xl tw-bg-sky-900/20 tw-border tw-border-sky-500/30 tw-px-4 tw-py-3 tw-mb-3 tw-flex tw-items-center tw-gap-3">
                    <span className="tw-text-lg">✈️</span>
                    <div>
                        <p className="tw-text-sm tw-font-semibold tw-text-sky-300">Travel Day: {currentDay.city}</p>
                        <p className="tw-text-xs tw-text-muted">You can still add activities before or after your flight</p>
                    </div>
                </div>
            )}

            {/* Loading state */}
            {loading && !isFlightDay && (
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-14">
                    <CircularProgress size={40} sx={{ color: '#818cf8' }} />
                    <p className="tw-text-sm tw-text-muted">Loading activity suggestions…</p>
                </div>
            )}

            {/* Empty state — shown when not loading, no suggestions loaded, and not a flight day */}
            {!loading && !isFlightDay && suggestedActivities.length === 0 && (
                <div className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-dashed tw-border-accent/30 tw-p-8 tw-mb-3 tw-text-center">
                    <div className="tw-flex tw-justify-center tw-mb-4">
                        <span className="tw-p-4 tw-rounded-full tw-bg-violet-500/10">
                            <AutoAwesomeIcon sx={{ fontSize: 40, color: '#818cf8' }} />
                        </span>
                    </div>
                    <h3 className="tw-text-lg tw-font-semibold tw-text-muted-fore tw-mb-2">
                        {t('ready_to_suggest') || 'Ready to suggest activities'}
                    </h3>
                    <p className="tw-text-sm tw-text-muted tw-mb-6 tw-max-w-xs tw-mx-auto">
                        {t('click_to_get_suggestions') || 'Click below to get AI-powered activity suggestions for this day.'}
                    </p>
                    <button
                        onClick={() => loadDayActivities(currentDayIndex)}
                        className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-accent tw-hover:tw-bg-accent-600 tw-text-white tw-font-semibold tw-px-6 tw-py-3 tw-rounded-2xl tw-shadow-lg tw-shadow-accent/20 tw-transition-all tw-duration-200 tw-hover:tw-scale-[1.03]"
                    >
                        <GeminiIcon />
                        {t('get_ai_suggestions') || 'Get AI Suggestions'}
                    </button>
                </div>
            )}
            
            {/* Activity suggestions */}
            {!loading && !isFlightDay && suggestedActivities.length > 0 && (
                <Box sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 3, background: 'rgba(22,27,34,0.88)', backdropFilter: 'blur(12px)', border: '1px solid rgba(99,102,241,0.18)' }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1rem' }, color: '#fff' }}>
                        {t('select_activities') || 'Select activities for this day:'}
                    </Typography>
                    
                    {/* Group activities by section */}
                    {(() => {
                        // Define section order and labels
                        const sectionOrder = ['highlight', 'nature', 'culture', 'food', 'adventure', 'relaxation', 'shopping', 'nightlife'];
                        const sectionLabels = {
                            highlight: { label: '🌟 Must-See Highlights', desc: 'Iconic attractions every visitor should experience' },
                            nature: { label: '🌿 Nature', desc: 'Parks, gardens, and outdoor spots' },
                            culture: { label: '🏛️ Culture', desc: 'Museums, history, and local traditions' },
                            food: { label: '🍜 Food', desc: 'Culinary experiences and local flavors' },
                            adventure: { label: '🎢 Adventure', desc: 'Exciting activities and unique experiences' },
                            relaxation: { label: '🧘 Relaxation', desc: 'Wellness and peaceful spots' },
                            shopping: { label: '🛍️ Shopping', desc: 'Markets, boutiques, and souvenirs' },
                            nightlife: { label: '🌙 Nightlife', desc: 'Evening entertainment and bars' }
                        };
                        
                        // Group activities by section
                        const groupedActivities = {};
                        suggestedActivities.forEach(activity => {
                            const section = activity.section || 'highlight';
                            if (!groupedActivities[section]) {
                                groupedActivities[section] = [];
                            }
                            groupedActivities[section].push(activity);
                        });
                        
                        // Render sections in order
                        return sectionOrder.map(section => {
                            const activities = groupedActivities[section];
                            if (!activities || activities.length === 0) return null;
                            
                            const sectionInfo = sectionLabels[section] || { label: section, desc: '' };
                            
                            return (
                                <Box key={section} sx={{ mb: 3 }}>
                                    {/* Section Header */}
                                    <Box sx={{ 
                                        mb: 1.5, 
                                        pb: 1, 
                                        borderBottom: section === 'highlight' ? '2px solid rgba(99,102,241,0.6)' : '1px solid rgba(99,102,241,0.2)'
                                    }}>
                                        <Typography 
                                            variant="subtitle2" 
                                            sx={{ 
                                                fontWeight: 700, 
                                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                                color: section === 'highlight' ? '#a5b4fc' : '#c9d1d9'
                                            }}
                                        >
                                            {sectionInfo.label}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#6e7681' }}>
                                            {sectionInfo.desc}
                                        </Typography>
                                    </Box>
                                    
                                    {/* Activities in this section */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 1 } }}>
                                        {activities.map((activity) => {
                            const imageData = getActivityImage(activity.name, currentDay.city);
                            const googleImagesUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(`${activity.name} ${currentDay.city} ${currentDay.country}`)}`;
                            
                            return (
                            <Box
                                key={activity.activity_id}
                                onClick={() => handleToggleActivity(activity.activity_id)}
                                sx={{
                                    p: { xs: 1.5, sm: 2 },
                                    border: '2px solid',
                                    borderColor: selectedActivityIds.has(activity.activity_id) 
                                        ? 'rgba(99,102,241,0.7)' 
                                        : 'rgba(99,102,241,0.18)',
                                    borderRadius: 2,
                                    background: selectedActivityIds.has(activity.activity_id)
                                        ? 'rgba(99,102,241,0.14)'
                                        : 'rgba(13,17,23,0.6)',
                                    backdropFilter: 'blur(8px)',
                                    WebkitBackdropFilter: 'blur(8px)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: selectedActivityIds.has(activity.activity_id) ? '#818cf8' : 'rgba(99,102,241,0.5)',
                                        background: selectedActivityIds.has(activity.activity_id) ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.06)'
                                    }
                                }}
                            >
                                {/* Mobile layout: Image on top, content below */}
                                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                    {/* Activity Image - Full width on mobile */}
                                    <Box
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(googleImagesUrl, '_blank');
                                        }}
                                        sx={{
                                            width: '100%',
                                            height: 120,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            mb: 1.5,
                                            cursor: 'pointer',
                                            border: '1px solid rgba(99,102,241,0.25)',
                                            bgcolor: 'rgba(22,27,34,0.7)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {imageData?.image_url ? (
                                            <img
                                                src={imageData.image_url}
                                                alt={activity.name}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.parentElement.innerHTML = '<span style="color:#888;font-size:14px;">📷 Tap for photos</span>';
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ textAlign: 'center', color: '#888' }}>
                                                <ImageSearchIcon sx={{ fontSize: 32 }} />
                                                <Typography variant="caption" display="block">Tap for photos</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    
                                    {/* Content on mobile */}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <Checkbox
                                            checked={selectedActivityIds.has(activity.activity_id)}
                                            sx={{ p: 0, color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#fff' } }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 0.5, color: '#1a1a2e' }}>
                                                {activity.name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                                                <Chip 
                                                    icon={<AccessTimeIcon sx={{ fontSize: '12px !important' }} />}
                                                    label={`${activity.duration_minutes || 60} min`}
                                                    size="small" 
                                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                                />
                                                {activity.category && (
                                                    <Chip label={activity.category} size="small" sx={{ height: 22, fontSize: '0.7rem' }} />
                                                )}
                                                {activity.is_recommended && (
                                                    <Chip label="⭐ Top Pick" size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#1a1a2e', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.4)' }} />
                                                )}
                                            </Box>
                                            {activity.description && (
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4, color: '#8b949e' }}>
                                                    {activity.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                                
                                {/* Desktop layout: Checkbox + Image + Content side by side */}
                                <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'flex-start', gap: 2 }}>
                                    <Checkbox
                                        checked={selectedActivityIds.has(activity.activity_id)}
                                        sx={{ p: 0, mt: 0.5, color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#fff' } }}
                                    />
                                    {/* Activity Image */}
                                    <Tooltip title="View Photos on Google">
                                        <Box
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.open(googleImagesUrl, '_blank');
                                            }}
                                            sx={{
                                                width: 100,
                                                height: 75,
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                flexShrink: 0,
                                                cursor: 'pointer',
                                                border: '1px solid rgba(99,102,241,0.25)',
                                                bgcolor: 'rgba(22,27,34,0.7)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                '&:hover': {
                                                    opacity: 0.85,
                                                    borderColor: 'rgba(99,102,241,0.5)'
                                                }
                                            }}
                                        >
                                            {imageData?.image_url ? (
                                                <img
                                                    src={imageData.image_url}
                                                    alt={activity.name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.style.display = 'none';
                                                        e.target.parentElement.innerHTML = '<svg style="color:#888;width:32px;height:32px" viewBox="0 0 24 24"><path fill="currentColor" d="M18 13v7H4V6h5.02c.05-.71.22-1.38.48-2H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-5l-2-2zm-1.5 5h-11l2.75-3.53 1.96 2.36 2.75-3.54L16.5 18zm2.8-9.11c.44-.7.7-1.51.7-2.39C20 4.01 17.99 2 15.5 2S11 4.01 11 6.5s2.01 4.5 4.5 4.5c.88 0 1.69-.26 2.39-.7L21 13.41 22.41 12 19.3 8.89zm-4.3.61C13.67 9.5 13 8.83 13 8s.67-1.5 1.5-1.5S16 7.17 16 8s-.67 1.5-1.5 1.5z"/></svg>';
                                                    }}
                                                />
                                            ) : (
                                                <ImageSearchIcon sx={{ color: '#888', fontSize: 28 }} />
                                            )}
                                        </Box>
                                    </Tooltip>
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                                                {activity.name}
                                            </Typography>
                                            {activity.is_recommended && (
                                                <Chip 
                                                    label="⭐ Top Pick"
                                                    size="small" 
                                                    sx={{ height: 20, bgcolor: '#1a1a2e', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.4)' }}
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', color: '#6e7681' }}>
                                            <span>
                                                <AccessTimeIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                                {activity.duration_minutes || 60} min
                                            </span>
                                            {activity.category && (
                                                <Chip label={activity.category} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                                            )}
                                        </Typography>
                                        {activity.description && (
                                            <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.85rem', color: '#8b949e' }}>
                                                {activity.description}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        );
                                        })}
                                    </Box>
                                </Box>
                            );
                        });
                    })()}
                    
                    {/* Get more suggestions button */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleGetMoreSuggestions}
                            disabled={loading}
                            sx={{ flex: 1, color: '#c9d1d9', borderColor: 'rgba(201,209,217,0.3)', textTransform: 'none', borderRadius: 2, '&:hover': { borderColor: '#6366f1', color: '#fff', background: 'rgba(99,102,241,0.1)' } }}
                        >
                            {t('get_more_suggestions') || 'Get More Suggestions'}
                        </Button>
                    </Box>
                </Box>
            )}
            
            {/* Action buttons */}
            <Box sx={{ position: 'sticky', bottom: 0, zIndex: 10, py: 1.5, px: 1, background: 'rgba(13,17,23,0.96)', backdropFilter: 'blur(10px)', borderTop: '1px solid rgba(99,102,241,0.2)', mx: -1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 1, sm: 2 }, width: '100%' }}>
                {/* Back button - hide in single day mode */}
                {!singleDayMode && currentDayIndex > 0 && (
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleGoBack}
                        disabled={loading || isAutoPlanning}
                        sx={{ order: { xs: 2, sm: 0 } }}
                    >
                        Back
                    </Button>
                )}
                
                {/* Close button — only in singleDayMode, always LEFT of confirm */}
                {singleDayMode && onClose && (
                    <Button
                        variant="contained"
                        onClick={onClose}
                        sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, minWidth: 0, px: 2, py: { xs: 1.5, sm: 1 }, flexShrink: 0 }}
                        aria-label="Close"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </Button>
                )}
                {/* Confirm day button */}
                {!isFlightDay && (
                    <Button
                        variant="contained"
                        color="primary"
                        endIcon={isLastDay ? <CheckCircleIcon /> : <ArrowForwardIcon />}
                        onClick={handleConfirmDay}
                        disabled={loading || isAutoPlanning}
                        sx={{ flex: 1, py: { xs: 1.5, sm: 1 }, ...(singleDayMode && { outline: '2px solid #22c55e', outlineOffset: '2px' }) }}
                    >
                        {singleDayMode
                            ? 'Add Selected Activities'
                            : isLastDay 
                                ? 'Complete Itinerary'
                                : 'Confirm & Continue'
                        }
                    </Button>
                )}
                
                {/* Auto-plan remaining button - hide in single day mode */}
                {!singleDayMode && !isLastDay && !isFlightDay && (
                    <Tooltip title="Let AI generate activities for all remaining days">
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={isAutoPlanning ? <CircularProgress size={16} /> : <FastForwardIcon />}
                            onClick={handleAutoPlanRemaining}
                            disabled={loading || isAutoPlanning}
                            sx={{ order: { xs: 3, sm: 0 } }}
                        >
                            {isAutoPlanning ? 'Planning...' : 'AI Finish All'}
                        </Button>
                    </Tooltip>
                )}
            </Box>
            </Box>
            
            {/* Cancel button */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                    variant="text"
                    color="inherit"
                    onClick={onCancel}
                    disabled={isAutoPlanning}
                >
                    Cancel
                </Button>
            </Box>
            
            {/* Floating Day Intensity Indicator with List Button — portal to escape backdrop-filter containing block */}
            {ReactDOM.createPortal(<Box
                sx={{
                    position: 'fixed',
                    bottom: 80,
                    right: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 1000,
                }}
            >
                {/* List button — hidden, hours circle serves same function */}
                
                {/* Hours indicator */}
                <Tooltip 
                    title={
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {dayIntensity.intensity === 'easy' && '😊 Easy Day'}
                                {dayIntensity.intensity === 'moderate' && '🚶 Moderate Day'}
                                {dayIntensity.intensity === 'intense' && '🔥 Intense Day'}
                            </Typography>
                            <Typography variant="caption">
                                {dayIntensity.activityCount} activities
                                {dayIntensity.existingCount > 0 && dayIntensity.selectedCount > 0 && (
                                    <span> ({dayIntensity.existingCount} existing + {dayIntensity.selectedCount} new)</span>
                                )}
                            </Typography>
                            <Typography variant="caption" display="block">
                                (includes 1hr buffer between activities)
                            </Typography>
                        </Box>
                    }
                    placement="left"
                >
                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            bgcolor: '#0d1117',
                            boxShadow: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '4px solid',
                            borderColor: dayIntensity.color,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'scale(1.1)',
                                boxShadow: 6,
                            }
                        }}
                        onClick={() => setShowActivityList(true)}
                    >
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                fontWeight: 'bold', 
                                color: dayIntensity.color,
                                lineHeight: 1,
                            }}
                        >
                            {dayIntensity.totalHours}
                        </Typography>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.65rem',
                                lineHeight: 1,
                            }}
                        >
                            hours
                        </Typography>
                        <AccessTimeIcon 
                            sx={{ 
                                fontSize: 14, 
                                color: dayIntensity.color,
                                mt: 0.25
                            }} 
                        />
                    </Box>
                </Tooltip>
            </Box>, document.body)}
            
            {/* Activity List Dialog */}
            <Dialog
                open={showActivityList}
                onClose={() => setShowActivityList(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {t('day_label')} {currentDayIndex + 1} - Activities ({dayIntensity.totalHours}h)
                    </Typography>
                    <IconButton onClick={() => setShowActivityList(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {/* Existing Activities Section */}
                    {(() => {
                        const existingDayActivities = (existingActivities[currentDayDate] || []).filter(a => a.type !== 'flight' && a.type !== 'hotel' && a.type !== 'accommodation');
                        
                        if (existingDayActivities.length > 0) {
                            return (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
                                        📋 Existing Activities ({existingDayActivities.length})
                                    </Typography>
                                    <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
                                        {existingDayActivities.map((activity, idx) => (
                                            <ListItem key={activity.id || idx}>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <CheckBoxIcon color="success" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={activity.title || activity.name}
                                                    secondary={`${activity.duration || 60} min`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            );
                        }
                        return null;
                    })()}
                    
                    {/* Selected New Activities Section */}
                    {(() => {
                        const selectedActivities = suggestedActivities.filter(a => selectedActivityIds.has(a.activity_id));
                        
                        if (selectedActivities.length > 0) {
                            return (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                                        ✅ Selected New Activities ({selectedActivities.length})
                                    </Typography>
                                    <List dense sx={{ bgcolor: 'primary.50', borderRadius: 1 }}>
                                        {selectedActivities.map((activity) => (
                                            <ListItem key={activity.activity_id}>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <CheckBoxIcon color="primary" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={activity.name}
                                                    secondary={`${activity.duration_minutes || 60} min • ${activity.category || 'activity'}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            );
                        }
                        return null;
                    })()}
                    
                    {/* Unselected Suggestions */}
                    {(() => {
                        const unselectedActivities = suggestedActivities.filter(a => !selectedActivityIds.has(a.activity_id));
                        
                        if (unselectedActivities.length > 0) {
                            return (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
                                        💡 Available Suggestions ({unselectedActivities.length})
                                    </Typography>
                                    <List dense>
                                        {unselectedActivities.slice(0, 10).map((activity) => (
                                            <ListItem 
                                                key={activity.activity_id}
                                                onClick={() => {
                                                    handleToggleActivity(activity.activity_id);
                                                }}
                                                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <CheckBoxOutlineBlankIcon color="disabled" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={activity.name}
                                                    secondary={`${activity.duration_minutes || 60} min • ${activity.category || 'activity'}`}
                                                />
                                            </ListItem>
                                        ))}
                                        {unselectedActivities.length > 10 && (
                                            <Typography variant="caption" color="textSecondary" sx={{ pl: 2 }}>
                                                ... and {unselectedActivities.length - 10} more suggestions
                                            </Typography>
                                        )}
                                    </List>
                                </Box>
                            );
                        }
                        return null;
                    })()}
                    
                    {/* Summary */}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 1.5,
                        bgcolor: dayIntensity.color + '20',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: dayIntensity.color
                    }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Total Day Duration:
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: dayIntensity.color }}>
                            {dayIntensity.totalHours} hours
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        Includes 1 hour buffer between activities for travel & rest
                    </Typography>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default InteractiveDailyActivityBuilder;
